import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import * as pdfjsLib from "pdfjs-dist";
import API_BASE_URL from "./config";
import "./ContractSign.css";

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

const CONSENT_TEXT = "I agree this electronic signature is legally binding, equivalent to my handwritten signature.";

function ContractSign() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [contract, setContract] = useState(null);
  const [eventInfo, setEventInfo] = useState(null);

  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pdfReady, setPdfReady] = useState(false);

  const [signerName, setSignerName] = useState("");
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [justSigned, setJustSigned] = useState(false);

  const containerRef = useRef(null);
  const pdfCanvasRef = useRef(null);
  const pdfDocRef = useRef(null);
  const renderTaskRef = useRef(null);
  const sigCanvasRef = useRef(null);
  const isDrawingRef = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/shared/${token}/contract`);
        if (!res.ok) throw new Error("Invalid link");
        const data = await res.json();
        if (!data.contract) throw new Error("No contract found for this event");
        setContract(data.contract);
        setEventInfo({ eventName: data.event_name, studioName: data.studio_name });
      } catch (err) {
        setError(err.message || "Failed to load contract");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const renderPage = useCallback(async (pageNum, doc) => {
    const pdfDoc = doc || pdfDocRef.current;
    const container = containerRef.current;
    const canvas = pdfCanvasRef.current;
    if (!pdfDoc || !container || !canvas) return;

    // Cancel any in-flight render before starting a new one on the same canvas.
    // pdf.js throws "Cannot use the same canvas during multiple render()
    // operations" outright, and two renders overlap easily here: StrictMode
    // double-invokes the loading effect in development, and flipping pages
    // quickly starts a second render while the first is still painting. The
    // result is an uncaught error and a contract page that never appears — for
    // a client who is being asked to sign it. PdfReview and PdfFeedbackViewer
    // already guard this way.
    if (renderTaskRef.current) {
      try { renderTaskRef.current.cancel(); } catch (e) { /* already settled */ }
      renderTaskRef.current = null;
    }

    const page = await pdfDoc.getPage(pageNum);
    const containerWidth = Math.min(container.clientWidth - 32, 800);
    const viewport = page.getViewport({ scale: 1 });
    const scale = containerWidth / viewport.width;
    const scaledViewport = page.getViewport({ scale });

    canvas.width = scaledViewport.width;
    canvas.height = scaledViewport.height;

    const ctx = canvas.getContext("2d");
    const renderTask = page.render({
      canvasContext: ctx,
      viewport: scaledViewport,
      annotationMode: pdfjsLib.AnnotationMode.DISABLE,
      renderInteractiveForms: false,
    });
    renderTaskRef.current = renderTask;

    try {
      await renderTask.promise;
    } catch (err) {
      // A cancelled render is this function superseding itself, not a failure.
      if (err?.name !== "RenderingCancelledException") throw err;
    } finally {
      if (renderTaskRef.current === renderTask) renderTaskRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!contract?.pdf_url) return;
    (async () => {
      try {
        const doc = await pdfjsLib.getDocument(contract.pdf_url).promise;
        pdfDocRef.current = doc;
        setPageCount(doc.numPages);
        // Rendering is the page effect's job. Doing it here too meant page 1
        // was drawn twice on every load — the second render racing the first.
        setPdfReady(true);
      } catch (err) {
        setError("Failed to render contract PDF: " + err.message);
      }
    })();
  }, [contract, renderPage]);

  useEffect(() => {
    // Errors here were previously an unhandled rejection: a page that failed to
    // draw left a blank canvas and told the signer nothing.
    if (pdfReady) renderPage(currentPage).catch((err) => setError("Failed to render contract PDF: " + err.message));
    return () => {
      if (renderTaskRef.current) {
        try { renderTaskRef.current.cancel(); } catch (e) { /* already settled */ }
        renderTaskRef.current = null;
      }
    };
  }, [currentPage, pdfReady, renderPage]);

  const getPos = (e) => {
    const canvas = sigCanvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) * (canvas.width / rect.width),
      y: (clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const startDraw = (e) => {
    e.preventDefault();
    isDrawingRef.current = true;
    const pos = getPos(e);
    const ctx = sigCanvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.strokeStyle = "#1e1b4b";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.moveTo(pos.x, pos.y);
  };

  const draw = (e) => {
    if (!isDrawingRef.current) return;
    e.preventDefault();
    const pos = getPos(e);
    const ctx = sigCanvasRef.current.getContext("2d");
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    setHasSignature(true);
  };

  const endDraw = () => { isDrawingRef.current = false; };

  const clearSignature = () => {
    const canvas = sigCanvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const handleSubmit = async () => {
    if (!signerName.trim()) { setError("Enter your full name"); return; }
    if (!hasSignature) { setError("Please draw your signature"); return; }
    if (!consentAccepted) { setError("Please accept the consent statement"); return; }
    setError("");
    setSubmitting(true);
    try {
      const signaturePng = sigCanvasRef.current.toDataURL("image/png");
      const res = await fetch(`${API_BASE_URL}/api/shared/${token}/contract/sign`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ signerName: signerName.trim(), signaturePng, consentAccepted }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to sign");
      setJustSigned(true);
    } catch (err) {
      setError(err.message || "Failed to sign contract");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="csg-status">Loading contract...</div>;
  if (error && !contract) return <div className="csg-status csg-status--error">{error}</div>;

  const alreadySigned = contract?.status === "signed" || justSigned;

  return (
    <div className="csg-page">
      <header className="csg-header">
        <h1>{eventInfo?.studioName || "Contract"}</h1>
        <p>{eventInfo?.eventName}</p>
      </header>

      <div className="csg-viewer" ref={containerRef}>
        <canvas ref={pdfCanvasRef} className="csg-pdf-canvas" />
        {pageCount > 1 && (
          <div className="csg-pager">
            <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage <= 1}>‹ Prev</button>
            <span>Page {currentPage} of {pageCount}</span>
            <button onClick={() => setCurrentPage((p) => Math.min(pageCount, p + 1))} disabled={currentPage >= pageCount}>Next ›</button>
          </div>
        )}
      </div>

      {alreadySigned ? (
        <div className="csg-signed-panel">
          <div className="csg-signed-check">✓</div>
          <h2>Contract signed</h2>
          <p>Thanks — a copy has been sent to the studio.</p>
          <a
            className="csg-download-btn"
            href={`${API_BASE_URL}/api/shared/${token}/contract/signed-file`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Download your copy
          </a>
        </div>
      ) : (
        <div className="csg-sign-panel">
          <h2>Sign this contract</h2>
          {error && <div className="csg-error">{error}</div>}

          <label className="csg-field-label">Full name</label>
          <input
            className="csg-name-input"
            value={signerName}
            onChange={(e) => setSignerName(e.target.value)}
            placeholder="Type your full name"
          />

          <label className="csg-field-label">Signature</label>
          <div className="csg-sig-box">
            <canvas
              ref={sigCanvasRef}
              className="csg-sig-canvas"
              width={500}
              height={160}
              onMouseDown={startDraw}
              onMouseMove={draw}
              onMouseUp={endDraw}
              onMouseLeave={endDraw}
              onTouchStart={startDraw}
              onTouchMove={draw}
              onTouchEnd={endDraw}
            />
            {!hasSignature && <span className="csg-sig-placeholder">Draw your signature here</span>}
          </div>
          <button className="csg-clear-btn" onClick={clearSignature}>Clear</button>

          <label className="csg-consent-row">
            <input
              type="checkbox"
              checked={consentAccepted}
              onChange={(e) => setConsentAccepted(e.target.checked)}
            />
            <span>{CONSENT_TEXT}</span>
          </label>

          <button className="csg-submit-btn" onClick={handleSubmit} disabled={submitting}>
            {submitting ? "Signing..." : "Sign Contract"}
          </button>
        </div>
      )}
    </div>
  );
}

export default ContractSign;
